import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
  Checkbox as MuiCheckbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  UnfoldMore,
  UnfoldLess,
  Search,
  Delete as DeleteIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
  ReceiptLong,
  Edit as EditIcon,
} from '@mui/icons-material';
import api from '../services/api';
import RegisterPaymentDialog from '../components/RegisterPaymentDialog';
import InvoiceDialog from '../components/invoicing/InvoiceDialog';
import type { OrderSummary, OrderDetail, PaymentInstallment } from '../types';
import { useColumnConfig, type ColumnDefinition } from '../hooks/useColumnConfig';
import { DraggableTableHeader } from '../components/DraggableTableHeader';
import { useSavedFilterViews } from '../hooks/useSavedFilterViews';
import { SavedViewsTabs } from '../components/SavedViewsTabs';
import TextField from '../components/common/TextField';
import NewSaleDialog from '../components/commercial/NewSaleDialog';
import PendingDeliveriesDialog from '../components/commercial/PendingDeliveriesDialog';

// ─── Color maps ───────────────────────────────────────────────────────────────

const OPPORTUNITY_TYPES: { value: string; color: string }[] = [
  { value: 'Reposición', color: '#2196F3' },
  { value: 'Compra', color: '#4CAF50' },
  { value: 'Online', color: '#9C27B0' },
  { value: 'Fason', color: '#FF9800' },
  { value: 'Evento', color: '#E91E63' },
  { value: 'New Logo', color: '#00BCD4' },
  { value: 'Influencer', color: '#673AB7' },
  { value: 'Consignación', color: '#795548' },
  { value: 'Interna', color: '#607D8B' },
  { value: 'Marketing', color: '#F44336' },
];

const DELIVERY_STATUS_COLORS: Record<string, string> = {
  'Pendiente entrega': '#FFC107',
  'Enviado': '#4CAF50',
  'Seguimiento especial': '#FFC107',
  'No aplica': '#9E9E9E',
  'Pendiente sin Stock': '#F44336',
  'Preparado no Enviado': '#7E57C2',
  'Pendiente preparación': '#FFC107',
  // Ciclo de planificación
  'Pendiente de planificación': '#FF9800',
  'Planificado': '#2196F3',
  'En curso': '#00ACC1',
  'Finalizado': '#2E7D32',
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  'Pendiente Cobro': '#FFC107',
  'Pagado': '#4CAF50',
  'Moroso/Vencido': '#F44336',
  'Pagado parcialmente': '#FFC107',
  'Cheque entregado/no cobrado': '#03A9F4',
  'Incobrable': '#800020',
};

const COMMISSION_STATUS_COLORS: Record<string, string> = {
  'Pagada': '#4CAF50',
  'No pagada': '#F44336',
  'No Aplica': '#9E9E9E',
};

const STAGE_COLORS: Record<string, string> = {
  'Proyección': '#2196F3',
  'Ganado': '#4CAF50',
  'Perdido': '#F44336',
  'Negociando': '#03A9F4',
  'Cotización Pendiente': '#FFC107',
};

const getTypeColor = (t?: string | null) => OPPORTUNITY_TYPES.find(x => x.value === t)?.color || '#9E9E9E';
const getDeliveryStatusColor = (s?: string | null) => (s ? DELIVERY_STATUS_COLORS[s] ?? '#9E9E9E' : '#9E9E9E');
const getPaymentStatusColor = (s?: string | null) => (s ? PAYMENT_STATUS_COLORS[s] ?? '#9E9E9E' : '#9E9E9E');
const getCommissionStatusColor = (s?: string | null) => (s ? COMMISSION_STATUS_COLORS[s] ?? '#9E9E9E' : '#9E9E9E');
const getStageColor = (s?: string | null) => (s ? STAGE_COLORS[s] ?? '#9E9E9E' : '#9E9E9E');

// ─── Canal de venta (origen) ──────────────────────────────────────────────────
const CHANNEL_COLORS: Record<string, string> = {
  'MercadoLibre': '#2D3277',
  'TiendaNube': '#1E88E5',
  'Shopify': '#5E8E3E',
  'WhatsApp': '#25D366',
  'Manual': '#607D8B',
};
// Deriva el canal a partir del nombre de canal o del prefijo de la referencia (ML-/TN-).
const getChannelLabel = (channelName?: string | null, reference?: string | null): string => {
  const ch = (channelName || '').toLowerCase();
  const ref = (reference || '').toUpperCase();
  if (ch.includes('mercado') || ch === 'ml' || ref.startsWith('ML-')) return 'MercadoLibre';
  if (ch.includes('tienda') || ch.includes('nube') || ref.startsWith('TN-')) return 'TiendaNube';
  if (ch.includes('shopify') || ref.startsWith('SH-')) return 'Shopify';
  if (ch.includes('whats') || ch.includes('wpp')) return 'WhatsApp';
  if (channelName && channelName.trim()) return channelName.trim();
  return 'Manual';
};
const getChannelColor = (label: string) => CHANNEL_COLORS[label] ?? '#607D8B';

/**
 * Ventas que vienen de Mercado Libre o Tienda Nube: traen el momento real de la venta, así que se muestran con fecha y
 * hora de Argentina (de createdAt; la fecha de cierre de estas es solo el día). Las cargadas a mano siguen con la fecha.
 */
const isOnlineOrder = (o: { channelName?: string | null; reference?: string | null }) => {
  const label = getChannelLabel(o.channelName, o.reference);
  return label === 'MercadoLibre' || label === 'TiendaNube' || label === 'Shopify';
};
const fmtDateTimeAr = (d?: string | null) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '');
};
const fmtSaleDate = (o: { channelName?: string | null; reference?: string | null; closeDate?: string | null; createdAt?: string | null }) =>
  isOnlineOrder(o) ? fmtDateTimeAr(o.createdAt) : fmtDate(o.closeDate ?? o.createdAt);

// ─── Dropdown options for edit form ───────────────────────────────────────────

const DELIVERY_STATUS_OPTIONS = [
  'Pendiente de planificación',
  'Planificado',
  'En curso',
  'Finalizado',
  'Pendiente entrega',
  'Pendiente sin Stock',
  'Preparado no Enviado',
  'Pendiente preparación',
  'Enviado',
  'Entregado',
];

const PAYMENT_STATUS_OPTIONS = [
  'Pendiente Cobro',
  'Pagado parcialmente',
  'Pagado',
  'Moroso/Vencido',
  'Incobrable',
];

const PAYMENT_METHOD_OPTIONS = [
  'Efectivo',
  'Cheque',
  'Transferencia',
  'Tarjeta',
  'MercadoPago',
  'CuentaCorriente',
  'Otro',
];

// Cada cuota nueva agrega 30 días al cronograma (mismo día del mes siguiente),
// pero los días son editables por fila.
const DEFAULT_INSTALLMENT_GAP_DAYS = 30;

// Distribuye un porcentaje total entre N cuotas en partes lo más parejas posible
// (sin perder centésimas por redondeo). La última cuota absorbe el resto.
function distributePercentages(count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor((100 / count) * 100) / 100;
  const arr = Array<number>(count).fill(base);
  const used = base * count;
  const remainder = Math.round((100 - used) * 100) / 100;
  arr[count - 1] = Math.round((arr[count - 1] + remainder) * 100) / 100;
  return arr;
}

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmtCurrency = (v?: number | null, opts?: { noDecimals?: boolean }) => {
  if (v == null) return '—';
  // Preserve cents only when the stored value actually has them, so round
  // amounts stay clean ($ 1.000) but $ 12,5 doesn't render as "$ 13".
  const hasDecimals = !opts?.noDecimals && Math.abs(v - Math.round(v)) > 0.005;
  return `$ ${v.toLocaleString('es-AR', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: opts?.noDecimals ? 0 : 2,
  })}`;
};

// Accepts both "12.5" and "12,5" so AR users can type with a comma.
const parseDecimal = (v: string): number | null => {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return isNaN(n) ? null : n;
};
// Formato seguro-TZ: parseamos YYYY-MM-DD por string-split en vez de pasarlo a `new Date()`,
// que interpreta el sufijo Z como UTC y al renderizar en AR (UTC-3) corre el día al anterior.
const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const iso = d.slice(0, 10);
  const [y, m, day] = iso.split('-');
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};
const fmtInitials = (name?: string | null) =>
  name
    ? name
        .split(/[\s,]+/)
        .map(w => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 3)
    : '';

// ─── Column definitions ──────────────────────────────────────────────────────

const ALL_COLUMNS: ColumnDefinition[] = [
  { id: 'name', label: 'Titulo' },
  { id: 'customerName', label: 'Cliente' },
  { id: 'assignedSeller', label: 'Vendedor' },
  { id: 'opportunityType', label: 'Tipo' },
  { id: 'canal', label: 'Canal' },
  { id: 'total', label: 'Valor', align: 'right' },
  { id: 'closeDate', label: 'Fecha de Cierre' },
  { id: 'createdAt', label: 'Fecha Creación' },
  { id: 'deliveryCondition', label: 'Condición Delivery' },
  { id: 'deliveryStatus', label: 'Estado Delivery' },
  { id: 'paymentStatus', label: 'Pago' },
  { id: 'externalReference', label: 'Oferta' },
  { id: 'receiptNumber', label: 'Factura' },
  { id: 'deliverySlipReference', label: 'Remito' },
  { id: 'receivedConfirmation', label: 'Recibido', align: 'center' },
  { id: 'notes', label: 'Notas' },
  { id: 'isReconciled', label: 'Conciliado', align: 'center' },
  { id: 'paidAmount', label: 'Abono', align: 'right' },
  { id: 'balance', label: 'Saldo', align: 'right' },
  { id: 'actualDeliveryDate', label: 'Fecha Entrega' },
  { id: 'paymentDueDate', label: 'Fecha Vto' },
  { id: 'paymentDate', label: 'Fecha Abono' },
  { id: 'daysOverdue', label: 'Días Atraso', align: 'right' },
  { id: 'commissionStatus', label: 'Comisión' },
  { id: 'commissionAmount', label: 'Monto Comisión', align: 'right' },
  { id: 'paymentMethod', label: 'Condición Pago' },
  { id: 'status', label: 'Etapa' },
];

// ─── Saved views (tabs) ──────────────────────────────────────────────────────

interface SalesView {
  id: string;
  name: string;
  isDefault?: boolean;
  filters: Record<string, any>;
  createdAt: string;
  visibleColumns?: string[];
}

const defaultViews: SalesView[] = [
  {
    id: 'por-entregar',
    name: 'Por entregar',
    isDefault: true,
    filters: {},
    createdAt: '',
    visibleColumns: [
      'customerName', 'canal', 'total', 'externalReference', 'receiptNumber', 'deliverySlipReference',
      'closeDate', 'deliveryCondition', 'deliveryStatus', 'receivedConfirmation', 'notes',
      'paymentStatus', 'paymentMethod', 'paidAmount', 'balance', 'actualDeliveryDate',
      'paymentDueDate', 'paymentDate', 'daysOverdue',
    ],
  },
  {
    id: 'pendiente-cobro',
    name: 'Pendiente de Cobro',
    isDefault: true,
    filters: {},
    createdAt: '',
    visibleColumns: [
      'customerName', 'total', 'paidAmount', 'balance', 'externalReference', 'deliverySlipReference', 'receiptNumber',
      'closeDate', 'paymentDueDate', 'daysOverdue', 'deliveryStatus', 'paymentStatus',
    ],
  },
  {
    id: 'morosos',
    name: 'Morosos',
    isDefault: true,
    filters: {},
    createdAt: '',
    visibleColumns: [
      'customerName', 'total', 'paidAmount', 'balance', 'externalReference', 'deliverySlipReference', 'receiptNumber',
      'closeDate', 'paymentDueDate', 'daysOverdue', 'deliveryStatus', 'paymentStatus',
    ],
  },
  {
    id: 'fason-ganado',
    name: 'Fason Ganado',
    isDefault: true,
    filters: {},
    createdAt: '',
    visibleColumns: ['customerName', 'total', 'receiptNumber', 'closeDate'],
  },
  {
    id: 'comisiones',
    name: 'Comisiones',
    isDefault: true,
    filters: {},
    createdAt: '',
    visibleColumns: [
      'assignedSeller', 'opportunityType', 'customerName', 'status', 'total',
      'receiptNumber', 'paymentStatus', 'commissionStatus', 'commissionAmount',
    ],
  },
];

// ─── Tab filter functions ────────────────────────────────────────────────────

const WON_STATUSES = ['Ganado', 'Won', 'Delivered', 'Shipped', 'Ready', 'InProgress', 'Pending'];
const PENDING_DELIVERY_STATUSES = ['Pendiente de planificación', 'Planificado', 'En curso', 'Pendiente entrega', 'Pendiente sin Stock', 'Preparado no Enviado', 'Pendiente preparación'];

function filterByTab(orders: OrderSummary[], tabId: string | null): OrderSummary[] {
  if (!tabId) return orders;

  switch (tabId) {
    case 'por-entregar':
      return orders.filter(o =>
        o.opportunityType !== 'Evento' &&
        o.opportunityType !== 'Online' &&
        WON_STATUSES.some(s => (o.status || '').toLowerCase() === s.toLowerCase()) &&
        PENDING_DELIVERY_STATUSES.includes(o.deliveryStatus || '')
      );
    case 'pendiente-cobro':
      return orders.filter(o =>
        ['Pendiente Cobro', 'Pagado parcialmente'].includes(o.paymentStatus || '')
      );
    case 'morosos':
      return orders.filter(o =>
        o.deliveryStatus === 'Enviado' &&
        ['Moroso/Vencido', 'Incobrable'].includes(o.paymentStatus || '')
      );
    case 'fason-ganado':
      return orders.filter(o =>
        o.opportunityType === 'Fason' &&
        WON_STATUSES.some(s => (o.status || '').toLowerCase() === s.toLowerCase())
      );
    case 'comisiones':
      return orders.filter(o =>
        WON_STATUSES.some(s => (o.status || '').toLowerCase() === s.toLowerCase()) &&
        (o.paymentStatus || '').toLowerCase() === 'pagado'
      );
    default:
      return orders;
  }
}

// ─── Month grouping ──────────────────────────────────────────────────────────

interface MonthGroup {
  key: string;
  label: string;
  orders: OrderSummary[];
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  orderCount: number;
  pendingCount: number;
}

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function groupOrdersByMonth(orders: OrderSummary[]): MonthGroup[] {
  const groups: Record<string, MonthGroup> = {};
  for (const order of orders) {
    // Group by closeDate when present so old sales loaded today land in the
    // month/year their close date belongs to. Fall back to payment due, then
    // createdAt only when neither business date is set.
    const raw = order.closeDate || order.paymentDueDate || order.createdAt;
    const date = new Date(raw);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const key = `${year}-${String(month + 1).padStart(2, '0')}`;
    if (!groups[key]) {
      groups[key] = {
        key,
        label: `${MONTH_NAMES[month]} ${year}`,
        orders: [],
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
        orderCount: 0,
        pendingCount: 0,
      };
    }
    const total = order.total || 0;
    const paid = order.paidAmount || 0;
    const balance = total - paid;
    groups[key].orders.push(order);
    groups[key].totalAmount += total;
    groups[key].paidAmount += paid;
    groups[key].pendingAmount += balance > 0 ? balance : 0;
    groups[key].orderCount += 1;
    if (balance > 0.5) groups[key].pendingCount += 1;
  }
  return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
}

// ─── Helper: render link-or-text for reference fields ────────────────────────

function renderLinkOrText(value?: string | null): React.ReactNode {
  if (!value) return '—';
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return (
      <Link href={value} target="_blank" rel="noopener noreferrer" variant="body2">
        {value.length > 30 ? value.slice(0, 30) + '...' : value}
      </Link>
    );
  }
  return value;
}

// ─── Computed fields ─────────────────────────────────────────────────────────

function computeBalance(o: OrderSummary): number {
  return (o.total || 0) - (o.paidAmount || 0);
}

function computeCommissionAmount(o: OrderSummary): number {
  if (o.commissionAmount != null) return o.commissionAmount;
  const pct = o.commissionPct != null ? o.commissionPct : 4;
  return (o.total || 0) * (pct / 100);
}

function computePaymentDueDate(o: OrderSummary): string | null {
  if (o.paymentDueDate) return o.paymentDueDate;
  if (!o.createdAt) return null;
  const d = new Date(o.createdAt);
  d.setDate(d.getDate() + 30);
  return d.toISOString();
}

function computeDaysOverdue(o: OrderSummary): number | null {
  if (o.daysOverdue != null) return o.daysOverdue;
  const due = computePaymentDueDate(o);
  if (!due) return null;
  const diff = Math.floor((Date.now() - new Date(due).getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

// ─── Cell renderer ───────────────────────────────────────────────────────────

function renderCell(colId: string, o: OrderSummary): React.ReactNode {
  switch (colId) {
    case 'name':
      return o.name || '—';
    case 'customerName':
      return o.customerName || '—';
    case 'assignedSeller':
      return o.assignedSeller ? (
        <Tooltip title={o.assignedSeller}>
          <Chip size="small" label={fmtInitials(o.assignedSeller)} />
        </Tooltip>
      ) : '—';
    case 'opportunityType':
      return o.opportunityType ? (
        <Chip size="small" label={o.opportunityType} sx={{ bgcolor: getTypeColor(o.opportunityType), color: '#fff' }} />
      ) : '—';
    case 'total':
      return fmtCurrency(o.total, { noDecimals: true });
    case 'closeDate':
      return fmtSaleDate(o);
    case 'createdAt':
      return isOnlineOrder(o) ? fmtDateTimeAr(o.createdAt) : fmtDate(o.createdAt);
    case 'canal': {
      const canal = getChannelLabel(o.channelName, o.reference);
      return <Chip size="small" label={canal} sx={{ bgcolor: getChannelColor(canal), color: '#fff' }} />;
    }
    case 'deliveryCondition':
      return o.deliveryCondition ? (
        <Chip size="small" label={o.deliveryCondition} variant="outlined" />
      ) : '—';
    case 'deliveryStatus':
      return o.deliveryStatus ? (
        <Chip size="small" label={o.deliveryStatus} sx={{ bgcolor: getDeliveryStatusColor(o.deliveryStatus), color: '#fff' }} />
      ) : '—';
    case 'paymentStatus':
      return o.paymentStatus ? (
        <Chip size="small" label={o.paymentStatus} sx={{ bgcolor: getPaymentStatusColor(o.paymentStatus), color: '#fff' }} />
      ) : '—';
    case 'externalReference':
      return renderLinkOrText(o.externalReference);
    case 'receiptNumber':
      return renderLinkOrText(o.receiptNumber);
    case 'deliverySlipReference':
      return renderLinkOrText(o.deliverySlipReference);
    case 'receivedConfirmation':
      return <MuiCheckbox size="small" checked={!!o.receivedConfirmation} disabled />;
    case 'notes':
      return o.notes || o.deliveryInfo || '—';
    case 'isReconciled':
      return <MuiCheckbox size="small" checked={!!o.isReconciled} disabled />;
    case 'paidAmount': {
      const paid = o.paidAmount || 0;
      return paid > 0 ? (
        <Typography variant="body2" color="success.main" fontWeight={600}>{fmtCurrency(paid)}</Typography>
      ) : (
        <Typography variant="body2" color="text.disabled">{fmtCurrency(0)}</Typography>
      );
    }
    case 'balance': {
      const bal = computeBalance(o);
      if (bal <= 0.5) {
        return <Typography variant="body2" color="success.main" fontWeight={600}>{fmtCurrency(0)}</Typography>;
      }
      const fullyPending = (o.paidAmount || 0) <= 0.5;
      return (
        <Typography variant="body2" color={fullyPending ? 'warning.main' : 'info.main'} fontWeight={600}>
          {fmtCurrency(bal)}
        </Typography>
      );
    }
    case 'actualDeliveryDate':
      return fmtDate(o.actualDeliveryDate);
    case 'paymentDueDate':
      return fmtDate(computePaymentDueDate(o));
    case 'paymentDate':
      return fmtDate(o.paymentDate);
    case 'daysOverdue': {
      const days = computeDaysOverdue(o);
      if (days == null) return '—';
      return days > 0 ? (
        <Typography variant="body2" color="error.main" fontWeight={600}>{days}</Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">0</Typography>
      );
    }
    case 'commissionStatus': {
      const cs = o.commissionStatus as string | undefined;
      return cs ? (
        <Chip size="small" label={cs} sx={{ bgcolor: getCommissionStatusColor(cs), color: '#fff' }} />
      ) : '—';
    }
    case 'commissionAmount':
      return fmtCurrency(computeCommissionAmount(o));
    case 'paymentMethod':
      return o.paymentMethod || '—';
    case 'status':
      return o.status ? (
        <Chip size="small" label={o.status} sx={{ bgcolor: getStageColor(o.status), color: '#fff' }} />
      ) : '—';
    default:
      return '—';
  }
}

// ─── Main component ──────────────────────────────────────────────────────────

export const SalesPage: React.FC = () => {
  // Data state
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search
  const [searchText, setSearchText] = useState('');

  // Month collapsing
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [allMonthsExpanded, setAllMonthsExpanded] = useState(false);

  // Order detail dialog (WI-10A)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Facturación electrónica (selección agrupada + diálogo)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceOrderIds, setInvoiceOrderIds] = useState<number[]>([]);
  const toggleSelect = (id: number) => setSelectedIds(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const openInvoice = (ids: number[]) => { setInvoiceOrderIds(ids); setInvoiceOpen(true); };

  // Register payment dialog
  const [paymentOpen, setPaymentOpen] = useState(false);

  // Editing state
  const [editing, setEditing] = useState(false);
  const [revertingDelivery, setRevertingDelivery] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [editSaving, setEditSaving] = useState(false);
  // editedItems: null = user did not touch items (don't send), array = explicit new item list
  type EditableItem = { productId: number; productName?: string | null; sku?: string | null; quantity: number; unitPrice?: number | null };
  const [editedItems, setEditedItems] = useState<EditableItem[] | null>(null);
  const [newItemDraft, setNewItemDraft] = useState<{ productId: number | null; productName?: string | null; sku?: string | null; quantity: number; unitPrice: number | null }>({ productId: null, quantity: 1, unitPrice: null });
  const [productOptions, setProductOptions] = useState<Array<{ id: number; name: string; sku?: string | null; price?: number }>>([]);

  // Edición inline del título (funciona también en ventas ya concretadas)
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);

  // Save view dialog
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  // Marcar en bloque como "Entregado" las ventas viejas (sin tocar stock)
  const [bulkDeliverOpen, setBulkDeliverOpen] = useState(false);
  const [bulkDeliverCutoff, setBulkDeliverCutoff] = useState('');
  const [bulkDeliverSaving, setBulkDeliverSaving] = useState(false);
  const [bulkDeliverResult, setBulkDeliverResult] = useState<number | null>(null);

  // Saved views
  const {
    views,
    activeViewId,
    activeView,
    saveView,
    deleteView,
    renameView,
    setActiveView,
  } = useSavedFilterViews({
    storageKey: 'sales-page-views',
    defaultViews: defaultViews as any,
  });

  // Determine which columns are visible based on active view
  const activeViewDef = useMemo(() => {
    if (!activeViewId) return null;
    return defaultViews.find(v => v.id === activeViewId) ?? null;
  }, [activeViewId]);

  // Column config
  const {
    columns,
    visibleColumns: baseVisibleColumns,
    columnConfigs,
    reorderColumns,
    toggleVisibility,
    resetToDefaults,
    isDefaultOrder,
  } = useColumnConfig({
    storageKey: 'sales-page-columns-v2',
    defaultColumns: ALL_COLUMNS,
  });

  // Apply tab-specific visible columns override
  const visibleColumns = useMemo(() => {
    if (activeViewDef?.visibleColumns) {
      const allowed = new Set(activeViewDef.visibleColumns);
      return ALL_COLUMNS.filter(c => allowed.has(c.id));
    }
    return baseVisibleColumns;
  }, [activeViewDef, baseVisibleColumns]);

  // Venta/pedido desde la PC y pedidos a entregar (flag "commercial-suite").
  const [commercialSuite, setCommercialSuite] = useState(false);
  const [newSaleOpen, setNewSaleOpen] = useState(false);
  const [deliveriesOpen, setDeliveriesOpen] = useState(false);
  const [pendingDeliveries, setPendingDeliveries] = useState(0);
  const refreshPendingDeliveries = () => {
    api.getPendingDeliveries().then(r => setPendingDeliveries(r.length)).catch(() => setPendingDeliveries(0));
  };
  useEffect(() => {
    api.getFeatureFlags()
      .then(flags => {
        const on = (flags || []).some(f => f.enabled && f.key.toLowerCase() === 'commercial-suite');
        setCommercialSuite(on);
        if (on) refreshPendingDeliveries();
      })
      .catch(() => setCommercialSuite(false));
  }, []);

  // ── Data loading ─────────────────────────────────────────────────────────

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getOrders({ page: 1, pageSize: 10000 });
      const nonCancelled = (res.items || []).filter(o => o.status !== 'Cancelled');
      setOrders(nonCancelled);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron cargar las ventas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  // ── Filter pipeline ─────────────────────────────────────────────────────

  const filteredOrders = useMemo(() => {
    // 1. Apply tab filter
    let result = filterByTab(orders, activeViewId);

    // 2. Apply text search across key fields
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(o =>
        (o.name || '').toLowerCase().includes(q) ||
        (o.customerName || '').toLowerCase().includes(q) ||
        (o.assignedSeller || '').toLowerCase().includes(q) ||
        (o.externalReference || '').toLowerCase().includes(q) ||
        (o.receiptNumber || '').toLowerCase().includes(q) ||
        (o.reference || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [orders, activeViewId, searchText]);

  // ── Month grouping ──────────────────────────────────────────────────────

  const monthGroups = useMemo(() => groupOrdersByMonth(filteredOrders), [filteredOrders]);

  // Expand most recent month on first load
  useEffect(() => {
    if (monthGroups.length > 0 && expandedMonths.size === 0) {
      setExpandedMonths(new Set([monthGroups[0].key]));
    }
  }, [monthGroups]);

  const toggleMonth = (key: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllMonths = () => {
    if (allMonthsExpanded) {
      setExpandedMonths(new Set());
    } else {
      setExpandedMonths(new Set(monthGroups.map(g => g.key)));
    }
    setAllMonthsExpanded(!allMonthsExpanded);
  };

  // ── Totals ──────────────────────────────────────────────────────────────

  const grandTotals = useMemo(() => {
    let total = 0;
    let paid = 0;
    let pending = 0;
    let pendingCount = 0;
    for (const o of filteredOrders) {
      const t = o.total || 0;
      const p = o.paidAmount || 0;
      const balance = t - p;
      total += t;
      paid += p;
      if (balance > 0) pending += balance;
      if (balance > 0.5) pendingCount += 1;
    }
    return { total, paid, pending, pendingCount };
  }, [filteredOrders]);
  const grandTotal = grandTotals.total;

  // ── Order detail (WI-10A) ───────────────────────────────────────────────

  const openOrderDetail = async (order: OrderSummary) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setTitleEditing(false);
    try {
      const detail = await api.getOrder(order.id);
      setDetailOrder(detail);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar detalle');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Delete order (WI-10B) ─────────────────────────────────────────────

  const handleDeleteOrder = async (id: number) => {
    if (!window.confirm('¿Eliminar esta venta? Si fue entregada, el stock se revertirá.')) return;
    try {
      await api.deleteOrder(id);
      setDetailOpen(false);
      await loadOrders();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al eliminar');
    }
  };

  // ── PDF download (WI-10C) ─────────────────────────────────────────────

  const handleDownloadPdf = async (orderId: number, type: 'invoice' | 'remito') => {
    try {
      const blob = type === 'invoice'
        ? await api.downloadOrderInvoicePdf(orderId)
        : await api.downloadOrderRemitoPdf(orderId);
      if (!blob || blob.size === 0) {
        setError(`No se pudo generar el ${type === 'invoice' ? 'Factura' : 'Remito'}: PDF vacío.`);
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type === 'invoice' ? 'factura' : 'remito'}-${orderId}.pdf`;
      // Firefox and some WebKit builds need the anchor attached to the DOM for click() to fire.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (e: any) {
      setError(e?.response?.data?.message || `Error al descargar ${type}`);
    }
  };

  // ── Edit order ──────────────────────────────────────────────────────────

  const fmtDateInput = (d?: string | null) => {
    if (!d) return '';
    // Use UTC components so an ISO date stored as 2024-12-15T00:00:00Z does not shift
    // to 2024-12-14 when rendered in Argentina (UTC-3).
    const date = new Date(d);
    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleRevertDelivery = async () => {
    if (!detailOrder) return;
    if (!window.confirm('Esto va a restaurar el stock descontado por esta venta y la va a dejar sin marcar como entregada, para poder editarla. ¿Continuar?')) return;
    setRevertingDelivery(true);
    setError(null);
    try {
      const updated = await api.revertOrderDelivery(detailOrder.id);
      setDetailOrder(updated);
      await loadOrders();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo revertir la entrega');
    } finally {
      setRevertingDelivery(false);
    }
  };

  const startEditing = () => {
    if (!detailOrder) return;
    // Seed installments from the saved schedule; default to a single 100% row
    // (Efectivo, 0 días) when the order doesn't have one yet.
    const seededInstallments: PaymentInstallment[] = (detailOrder.paymentInstallments && detailOrder.paymentInstallments.length > 0)
      ? detailOrder.paymentInstallments.map((p, idx) => ({
          id: p.id,
          sequence: p.sequence ?? idx + 1,
          percentage: p.percentage,
          paymentMethod: p.paymentMethod || 'Efectivo',
          defermentDays: p.defermentDays ?? 0,
        }))
      : [{ sequence: 1, percentage: 100, paymentMethod: 'Efectivo', defermentDays: 0 }];
    setEditForm({
      deliveryStatus: detailOrder.deliveryStatus || '',
      paymentStatus: detailOrder.paymentStatus || '',
      notes: detailOrder.notes || '',
      receiptNumber: detailOrder.receiptNumber || '',
      deliverySlipReference: detailOrder.deliverySlipReference || '',
      externalReference: detailOrder.externalReference || '',
      paymentDueDate: fmtDateInput(detailOrder.paymentDueDate),
      paymentDate: fmtDateInput(detailOrder.paymentDate),
      actualDeliveryDate: fmtDateInput(detailOrder.actualDeliveryDate),
      closeDate: fmtDateInput(detailOrder.closeDate),
      paidAmount: detailOrder.paidAmount ?? 0,
      appliesIva: detailOrder.appliesIva ?? true,
      installments: seededInstallments,
    });
    setEditedItems(null);
    setNewItemDraft({ productId: null, quantity: 1, unitPrice: null });
    setEditing(true);
    // Fetch a wide page of products for the Agregar-producto selector. Runs once per edit session.
    if (productOptions.length === 0) {
      api.getProducts({ page: 1, pageSize: 500 } as any)
        .then(res => {
          const items = (res as any)?.products || (res as any)?.items || (res as any)?.data || [];
          setProductOptions(items.map((p: any) => ({ id: p.id, name: p.name, sku: p.sku, price: p.price })));
        })
        .catch(() => {});
    }
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditForm({});
    setEditedItems(null);
    setNewItemDraft({ productId: null, quantity: 1, unitPrice: null });
  };

  const handleEditChange = (field: string, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  // ── Cuotas (cronograma de pagos) ──────────────────────────────────────
  // Cada cuota tiene un porcentaje (deben sumar 100), un método de pago y
  // los días de diferimiento desde la Fecha de Cierre. Reemplaza al antiguo
  // campo libre "Condicion Pago".

  const getInstallments = (): PaymentInstallment[] =>
    (editForm.installments as PaymentInstallment[] | undefined) || [];

  const setInstallments = (next: PaymentInstallment[]) => {
    handleEditChange('installments', next.map((it, idx) => ({ ...it, sequence: idx + 1 })));
  };

  const handleAddInstallment = () => {
    const cur = getInstallments();
    const count = cur.length + 1;
    const pcts = distributePercentages(count);
    const next: PaymentInstallment[] = cur.map((it, idx) => ({ ...it, percentage: pcts[idx] }));
    const lastDays = cur.length > 0 ? cur[cur.length - 1].defermentDays : 0;
    next.push({
      sequence: count,
      percentage: pcts[count - 1],
      paymentMethod: cur.length > 0 ? cur[cur.length - 1].paymentMethod : 'Efectivo',
      defermentDays: lastDays + DEFAULT_INSTALLMENT_GAP_DAYS,
    });
    setInstallments(next);
  };

  const handleRemoveInstallment = (index: number) => {
    const cur = getInstallments();
    if (cur.length <= 1) return; // siempre debe quedar al menos una cuota
    const remaining = cur.filter((_, i) => i !== index);
    const pcts = distributePercentages(remaining.length);
    setInstallments(remaining.map((it, i) => ({ ...it, percentage: pcts[i] })));
  };

  const handleInstallmentField = (index: number, field: keyof PaymentInstallment, value: any) => {
    const cur = getInstallments();
    const next = cur.map((it, i) => (i === index ? { ...it, [field]: value } : it));
    setInstallments(next);
  };

  const installmentsTotalPct = useMemo(() => {
    const cur = getInstallments();
    return Math.round(cur.reduce((s, it) => s + (Number(it.percentage) || 0), 0) * 100) / 100;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editForm.installments]);

  const handleSaveTitle = async () => {
    if (!detailOrder) return;
    setTitleSaving(true);
    try {
      const res = await api.updateOrderName(detailOrder.id, titleDraft.trim() || null);
      setDetailOrder({ ...detailOrder, name: res.name });
      setTitleEditing(false);
      await loadOrders();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al guardar el título');
    } finally {
      setTitleSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!detailOrder) return;
    const installments = getInstallments();
    if (installments.length > 0 && Math.abs(installmentsTotalPct - 100) > 0.01) {
      setError(`Las cuotas deben sumar 100% (actual: ${installmentsTotalPct}%)`);
      return;
    }
    setEditSaving(true);
    try {
      const payload: Record<string, any> = {
        // Only send items when they were actually modified in this edit session.
        // Sending items forces the backend to re-clear and recalc Subtotal/Total,
        // which zeroes out historical imports that have Total but no line items.
        items: editedItems
          ? editedItems.map(i => ({ productId: i.productId, quantity: i.quantity, unitPrice: i.unitPrice }))
          : [],
        // Pass through existing base fields
        customerId: detailOrder.customerId,
        customerName: detailOrder.customerName,
        notes: editForm.notes || null,
        shippingAddress: detailOrder.shippingAddress,
        shippingCost: detailOrder.shippingCost || 0,
        fees: detailOrder.fees || 0,
        discount: detailOrder.discount || 0,
        // Editable Monday.com fields
        deliveryStatus: editForm.deliveryStatus || null,
        paymentStatus: editForm.paymentStatus || null,
        receiptNumber: editForm.receiptNumber || null,
        deliverySlipReference: editForm.deliverySlipReference || null,
        externalReference: editForm.externalReference || null,
        paymentDueDate: editForm.paymentDueDate || null,
        paymentDate: editForm.paymentDate || null,
        actualDeliveryDate: editForm.actualDeliveryDate || null,
        closeDate: editForm.closeDate || null,
        paidAmount: editForm.paidAmount != null && editForm.paidAmount !== '' ? Number(editForm.paidAmount) : null,
        appliesIva: editForm.appliesIva,
        // Cuotas: el backend re-deriva el string "Condicion Pago" a partir de
        // los métodos de pago de las cuotas.
        paymentInstallments: installments.map(it => ({
          percentage: Number(it.percentage) || 0,
          paymentMethod: it.paymentMethod || 'Efectivo',
          defermentDays: Number(it.defermentDays) || 0,
        })),
      };
      const updated = await api.updateOrder(detailOrder.id, payload);
      setDetailOrder(updated);
      setEditing(false);
      setEditForm({});
      await loadOrders();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al guardar cambios');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Save view handler ──────────────────────────────────────────────────

  const handleSaveView = () => {
    setSaveViewDialogOpen(true);
    setNewViewName('');
  };

  const handleSaveViewConfirm = () => {
    if (newViewName.trim()) {
      saveView(newViewName.trim(), {});
    }
    setSaveViewDialogOpen(false);
    setNewViewName('');
  };

  const handleBulkMarkDelivered = async () => {
    if (!bulkDeliverCutoff) return;
    setBulkDeliverSaving(true);
    setError(null);
    try {
      const { updated } = await api.bulkMarkOrdersDelivered(bulkDeliverCutoff);
      setBulkDeliverResult(updated);
      await loadOrders();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo marcar las ventas como entregadas');
    } finally {
      setBulkDeliverSaving(false);
    }
  };

  const closeBulkDeliverDialog = () => {
    setBulkDeliverOpen(false);
    setBulkDeliverCutoff('');
    setBulkDeliverResult(null);
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" spacing={2} alignItems="baseline">
          <Typography variant="h4" fontWeight={700}>Ventas</Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredOrders.length} ventas
          </Typography>
          {commercialSuite && (
            <Stack direction="row" spacing={1} sx={{ alignSelf: 'center' }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewSaleOpen(true)}>Nueva venta / pedido</Button>
              <Button variant="outlined" onClick={() => setDeliveriesOpen(true)}>
                Pedidos a entregar{pendingDeliveries > 0 ? ` (${pendingDeliveries})` : ''}
              </Button>
            </Stack>
          )}
        </Stack>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Tooltip title="Suma de todas las ventas (incluye pendientes de cobro)">
            <Stack alignItems="flex-end">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Facturado
              </Typography>
              <Typography variant="h6" fontWeight={600} color="primary.main" sx={{ lineHeight: 1.2 }}>
                {fmtCurrency(grandTotals.total)}
              </Typography>
            </Stack>
          </Tooltip>
          <Tooltip title="Plata efectivamente cobrada (suma de Abono). Coincide con Ingresos en Flujo de Caja.">
            <Stack alignItems="flex-end">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Cobrado
              </Typography>
              <Typography variant="h6" fontWeight={600} color="success.main" sx={{ lineHeight: 1.2 }}>
                {fmtCurrency(grandTotals.paid)}
              </Typography>
            </Stack>
          </Tooltip>
          <Tooltip title="Saldo aún no cobrado (Facturado − Cobrado)">
            <Stack alignItems="flex-end">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Pendiente {grandTotals.pendingCount > 0 ? `(${grandTotals.pendingCount})` : ''}
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                color={grandTotals.pending > 0 ? 'warning.main' : 'text.disabled'}
                sx={{ lineHeight: 1.2 }}
              >
                {fmtCurrency(grandTotals.pending)}
              </Typography>
            </Stack>
          </Tooltip>
          <Tooltip title={allMonthsExpanded ? 'Colapsar todos los meses' : 'Expandir todos los meses'}>
            <IconButton onClick={toggleAllMonths} size="small">
              {allMonthsExpanded ? <UnfoldLess /> : <UnfoldMore />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Barra de facturación agrupada */}
      {selectedIds.size > 0 && (() => {
        const selected = orders.filter(o => selectedIds.has(o.id));
        const customerIds = new Set(selected.map(o => o.customerId ?? 0));
        const sameCustomer = customerIds.size === 1;
        return (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
            <Typography variant="body2">{selectedIds.size} venta(s) seleccionada(s)</Typography>
            {!sameCustomer && (
              <Typography variant="caption" color="warning.main">
                Solo se pueden agrupar ventas de un mismo cliente.
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setSelectedIds(new Set())}>Limpiar</Button>
            <Tooltip title={sameCustomer ? '' : 'Seleccioná ventas del mismo cliente'}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ReceiptLong />}
                  disabled={!sameCustomer}
                  onClick={() => openInvoice(Array.from(selectedIds))}
                >
                  Facturar {selectedIds.size} ventas
                </Button>
              </span>
            </Tooltip>
          </Stack>
        );
      })()}

      {/* Tabs */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <SavedViewsTabs
          views={views}
          activeViewId={activeViewId}
          onSelectView={setActiveView}
          onSaveCurrentView={handleSaveView}
          onDeleteView={deleteView}
          onRenameView={renameView}
          showAllTab={true}
          allTabLabel="Todas"
        />
        {activeViewId === 'por-entregar' && (
          <Button size="small" variant="outlined" onClick={() => setBulkDeliverOpen(true)}>
            Marcar ventas viejas como entregadas
          </Button>
        )}
      </Stack>

      {/* Search bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por cliente, nombre, referencia..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
          }}
          sx={{ width: 400 }}
        />
      </Box>

      {/* Errors */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!loading && filteredOrders.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No se encontraron ventas para los filtros seleccionados.
        </Alert>
      )}

      {/* Month groups */}
      {!loading && monthGroups.map(group => {
        const expanded = expandedMonths.has(group.key);
        const isMlOrder = (o: OrderSummary) => {
          const ch = (o.channelName || '').toLowerCase();
          if (ch.includes('mercadolibre') || ch.includes('mercado libre') || ch === 'ml') return true;
          return (o.reference || '').toUpperCase().startsWith('ML-');
        };
        const mlOrders = group.orders.filter(isMlOrder);
        const nonMlOrders = group.orders.filter(o => !isMlOrder(o));
        const mlTotal = mlOrders.reduce((s, o) => s + (o.total || 0), 0);
        const mlExpandKey = `${group.key}::ml`;
        const mlExpanded = expandedMonths.has(mlExpandKey);
        return (
          <Card key={group.key} sx={{ mb: 2 }} variant="outlined">
            {/* Month header */}
            <Box
              onClick={() => toggleMonth(group.key)}
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                bgcolor: expanded ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <IconButton
                  size="small"
                  onClick={e => { e.stopPropagation(); toggleMonth(group.key); }}
                >
                  {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                <Typography variant="h6" fontWeight={600}>{group.label}</Typography>
                <Chip label={`${group.orderCount} ventas`} size="small" />
              </Stack>
              <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Stack alignItems="flex-end">
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Facturado
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="primary.main" sx={{ lineHeight: 1.2 }}>
                    {fmtCurrency(group.totalAmount)}
                  </Typography>
                </Stack>
                <Stack alignItems="flex-end">
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Cobrado
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} color="success.main" sx={{ lineHeight: 1.2 }}>
                    {fmtCurrency(group.paidAmount)}
                  </Typography>
                </Stack>
                <Stack alignItems="flex-end">
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Pendiente {group.pendingCount > 0 ? `(${group.pendingCount})` : ''}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color={group.pendingAmount > 0 ? 'warning.main' : 'text.disabled'}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {fmtCurrency(group.pendingAmount)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Collapsible table */}
            <Collapse in={expanded} unmountOnExit>
              <Divider />
              <TableContainer>
                <Table size="small">
                  <DraggableTableHeader
                    columns={columns}
                    visibleColumns={visibleColumns}
                    columnConfigs={columnConfigs}
                    onReorder={reorderColumns}
                    onToggleVisibility={toggleVisibility}
                    onResetToDefaults={resetToDefaults}
                    isDefaultOrder={isDefaultOrder}
                    leadingCell={<TableCell padding="checkbox" />}
                    trailingCell={<TableCell sx={{ width: 56 }} align="center">Facturar</TableCell>}
                  />
                  <TableBody>
                    {mlOrders.length > 0 && (
                      <>
                        <TableRow
                          hover
                          onClick={() => toggleMonth(mlExpandKey)}
                          sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}
                        >
                          <TableCell colSpan={visibleColumns.length + 3} sx={{ whiteSpace: 'nowrap' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {mlExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                              <Chip size="small" label="MercadoLibre" color="warning" />
                              <Typography variant="body2">
                                {mlOrders.length} ventas — <b>{fmtCurrency(mlTotal)}</b>
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {mlExpanded && mlOrders.map(o => (
                          <TableRow key={o.id} hover onClick={() => openOrderDetail(o)} sx={{ cursor: 'pointer' }}>
                            <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                              <MuiCheckbox size="small" checked={selectedIds.has(o.id)} onChange={() => toggleSelect(o.id)} />
                            </TableCell>
                            {visibleColumns.map(col => (
                              <TableCell key={col.id} align={col.align || 'left'} sx={{ whiteSpace: 'nowrap', pl: col.id === 'name' ? 4 : undefined }}>
                                {renderCell(col.id, o)}
                              </TableCell>
                            ))}
                            <TableCell align="center" onClick={e => e.stopPropagation()}>
                              <Tooltip title="Facturar (CAE)">
                                <IconButton size="small" color="primary" onClick={() => openInvoice([o.id])}>
                                  <ReceiptLong fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                    {nonMlOrders.map(o => (
                      <TableRow key={o.id} hover onClick={() => openOrderDetail(o)} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                          <MuiCheckbox size="small" checked={selectedIds.has(o.id)} onChange={() => toggleSelect(o.id)} />
                        </TableCell>
                        {visibleColumns.map(col => (
                          <TableCell key={col.id} align={col.align || 'left'} sx={{ whiteSpace: 'nowrap' }}>
                            {renderCell(col.id, o)}
                          </TableCell>
                        ))}
                        <TableCell align="center" onClick={e => e.stopPropagation()}>
                          <Tooltip title="Facturar (CAE)">
                            <IconButton size="small" color="primary" onClick={() => openInvoice([o.id])}>
                              <ReceiptLong fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Card>
        );
      })}

      {/* Order detail dialog (WI-10A / 10B / 10C) */}
      <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); cancelEditing(); setTitleEditing(false); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editing ? 'Editar Venta' : 'Detalle de Venta'} {detailOrder?.reference || ''}
            </Typography>
            <Stack direction="row" spacing={1}>
              {detailOrder && !editing && (
                <>
                  <Button size="small" variant="outlined" color="primary" startIcon={<ReceiptLong />} onClick={() => openInvoice([detailOrder.id])}>Facturar (CAE)</Button>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadPdf(detailOrder.id, 'invoice')}>Factura PDF</Button>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadPdf(detailOrder.id, 'remito')}>Remito PDF</Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<PaymentIcon />}
                    onClick={() => setPaymentOpen(true)}
                  >
                    Registrar pago
                  </Button>
                  {detailOrder.inventoryAdjusted === true && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={handleRevertDelivery}
                      disabled={revertingDelivery}
                    >
                      {revertingDelivery ? 'Revirtiendo...' : 'Revertir entrega'}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={startEditing}
                    disabled={detailOrder.inventoryAdjusted === true}
                  >
                    Editar
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {detailLoading && <CircularProgress />}
          {detailOrder && !detailLoading && (
            <Stack spacing={2}>
              {detailOrder.inventoryAdjusted && !editing && (
                <Alert severity="info">
                  Esta venta ya descontó stock. Para modificar productos o datos, primero tocá "Revertir entrega" arriba.
                </Alert>
              )}
              {/* Título editable (también en ventas concretadas: no toca stock ni montos) */}
              <Stack direction="row" spacing={1} alignItems="center">
                {titleEditing ? (
                  <>
                    <TextField
                      size="small"
                      label="Título"
                      value={titleDraft}
                      onChange={e => setTitleDraft(e.target.value)}
                      inputProps={{ maxLength: 200 }}
                      sx={{ minWidth: 320, flexGrow: 1, maxWidth: 560 }}
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') setTitleEditing(false); }}
                    />
                    <Button size="small" variant="contained" disabled={titleSaving} onClick={handleSaveTitle}>Guardar</Button>
                    <Button size="small" disabled={titleSaving} onClick={() => setTitleEditing(false)}>Cancelar</Button>
                  </>
                ) : (
                  <>
                    <Typography variant="body2">Título: <b>{detailOrder.name || '—'}</b></Typography>
                    <Tooltip title="Editar título">
                      <IconButton size="small" onClick={() => { setTitleDraft(detailOrder.name || ''); setTitleEditing(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
              {/* Read-only header info */}
              {(() => {
                const total = detailOrder.total || 0;
                const paid = detailOrder.paidAmount || 0;
                const saldo = total - paid;
                return (
                  <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
                    <Typography variant="body2">Cliente: <b>{detailOrder.customerName || '—'}</b></Typography>
                    <Typography variant="body2">Vendedor: <b>{detailOrder.assignedSeller || '—'}</b></Typography>
                    <Typography variant="body2">Fecha: <b>{fmtSaleDate(detailOrder)}</b></Typography>
                    <Typography variant="body2">Total: <b>{fmtCurrency(total)}</b></Typography>
                    <Typography variant="body2" color={paid > 0 ? 'success.main' : 'text.secondary'}>
                      Abono: <b>{fmtCurrency(paid)}</b>
                    </Typography>
                    <Typography
                      variant="body2"
                      color={saldo > 0.5 ? 'warning.main' : 'success.main'}
                      fontWeight={600}
                    >
                      Saldo: <b>{fmtCurrency(Math.max(0, saldo))}</b>
                    </Typography>
                    {!editing && detailOrder.deliveryStatus && <Chip size="small" label={detailOrder.deliveryStatus} sx={{ bgcolor: getDeliveryStatusColor(detailOrder.deliveryStatus), color: '#fff' }} />}
                    {!editing && detailOrder.paymentStatus && <Chip size="small" label={detailOrder.paymentStatus} sx={{ bgcolor: getPaymentStatusColor(detailOrder.paymentStatus), color: '#fff' }} />}
                  </Stack>
                );
              })()}

              {/* Editable fields */}
              {editing ? (
                <>
                  <Divider />
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>Estado</Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <TextField
                      select
                      label="Estado Delivery"
                      size="small"
                      value={editForm.deliveryStatus || ''}
                      onChange={e => handleEditChange('deliveryStatus', e.target.value)}
                      sx={{ minWidth: 220 }}
                    >
                      <MenuItem value="">— Sin estado —</MenuItem>
                      {DELIVERY_STATUS_OPTIONS.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Estado Pago"
                      size="small"
                      value={editForm.paymentStatus || ''}
                      onChange={e => handleEditChange('paymentStatus', e.target.value)}
                      sx={{ minWidth: 220 }}
                    >
                      <MenuItem value="">— Sin estado —</MenuItem>
                      {PAYMENT_STATUS_OPTIONS.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <Divider />
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>Cobro e IVA</Typography>
                  {/* IVA: precios netos +21% (ventas directas/B2B) vs. Total ya final (ML/integraciones) */}
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <MuiCheckbox
                      size="small"
                      checked={editForm.appliesIva !== false}
                      onChange={e => handleEditChange('appliesIva', e.target.checked)}
                      sx={{ p: 0.5 }}
                    />
                    <Box>
                      <Typography variant="body2">Precios netos — sumar IVA (21%)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Destildá si el precio ya incluye IVA (ej. ventas de MercadoLibre). El Valor del listado refleja esto.
                      </Typography>
                    </Box>
                  </Stack>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">Cuotas (cronograma de cobro)</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="caption"
                          color={Math.abs(installmentsTotalPct - 100) > 0.01 ? 'error.main' : 'text.secondary'}
                        >
                          Total: {installmentsTotalPct}%
                        </Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={handleAddInstallment}>
                          Agregar cuota
                        </Button>
                      </Stack>
                    </Stack>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 50 }}>#</TableCell>
                          <TableCell sx={{ width: 110 }}>%</TableCell>
                          <TableCell sx={{ width: 150 }}>Monto</TableCell>
                          <TableCell sx={{ width: 200 }}>Método</TableCell>
                          <TableCell sx={{ width: 130 }}>Días dif.</TableCell>
                          <TableCell sx={{ width: 60 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getInstallments().map((it, idx) => {
                          const total = detailOrder?.total || 0;
                          const amount = Math.round(total * (Number(it.percentage) || 0) / 100 * 100) / 100;
                          return (
                            <TableRow key={idx}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                                  value={Math.round((Number(it.percentage) || 0) * 100) / 100}
                                  onChange={e => handleInstallmentField(idx, 'percentage', e.target.value === '' ? 0 : Number(e.target.value))}
                                  sx={{ width: 100 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 0, step: 'any' }}
                                  value={Math.round(amount)}
                                  onChange={e => {
                                    const val = e.target.value === '' ? 0 : Number(e.target.value);
                                    const pct = total > 0 ? (val / total) * 100 : 0;
                                    handleInstallmentField(idx, 'percentage', pct);
                                  }}
                                  sx={{ width: 130 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  select
                                  size="small"
                                  value={it.paymentMethod}
                                  onChange={e => handleInstallmentField(idx, 'paymentMethod', e.target.value)}
                                  sx={{ minWidth: 180 }}
                                >
                                  {PAYMENT_METHOD_OPTIONS.map(m => (
                                    <MenuItem key={m} value={m}>{m}</MenuItem>
                                  ))}
                                </TextField>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 0, step: 1 }}
                                  value={it.defermentDays}
                                  onChange={e => handleInstallmentField(idx, 'defermentDays', e.target.value === '' ? 0 : Number(e.target.value))}
                                  sx={{ width: 100 }}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveInstallment(idx)}
                                  disabled={getInstallments().length <= 1}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                  <Divider />
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>Comprobantes y referencias</Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <TextField
                      label="Oferta (Referencia externa)"
                      size="small"
                      value={editForm.externalReference || ''}
                      onChange={e => handleEditChange('externalReference', e.target.value)}
                      sx={{ minWidth: 250 }}
                    />
                    <TextField
                      label="Factura (Comprobante)"
                      size="small"
                      value={editForm.receiptNumber || ''}
                      onChange={e => handleEditChange('receiptNumber', e.target.value)}
                      sx={{ minWidth: 250 }}
                    />
                    <TextField
                      label="Remito"
                      size="small"
                      value={editForm.deliverySlipReference || ''}
                      onChange={e => handleEditChange('deliverySlipReference', e.target.value)}
                      sx={{ minWidth: 250 }}
                    />
                  </Stack>
                  <Divider />
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>Fechas y abono</Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <TextField
                      label="Fecha de Cierre"
                      size="small"
                      type="date"
                      value={editForm.closeDate || ''}
                      onChange={e => handleEditChange('closeDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 170 }}
                    />
                    <TextField
                      label="Fecha Entrega"
                      size="small"
                      type="date"
                      value={editForm.actualDeliveryDate || ''}
                      onChange={e => handleEditChange('actualDeliveryDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 170 }}
                    />
                    <TextField
                      label="Fecha Vto"
                      size="small"
                      type="date"
                      value={editForm.paymentDueDate || ''}
                      onChange={e => handleEditChange('paymentDueDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 170 }}
                    />
                    <TextField
                      label="Fecha Abono"
                      size="small"
                      type="date"
                      value={editForm.paymentDate || ''}
                      onChange={e => handleEditChange('paymentDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 170 }}
                    />
                    <TextField
                      label="Abono ($)"
                      size="small"
                      type="text"
                      value={editForm.paidAmount ?? ''}
                      inputProps={{ inputMode: 'decimal', pattern: '[0-9.,]*' }}
                      onChange={e => handleEditChange('paidAmount', e.target.value.replace(',', '.'))}
                      sx={{ minWidth: 150 }}
                    />
                  </Stack>
                  <Divider />
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>Notas</Typography>
                  <TextField
                    label="Notas"
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={5}
                    value={editForm.notes || ''}
                    onChange={e => handleEditChange('notes', e.target.value)}
                    fullWidth
                  />
                </>
              ) : (
                <>
                  {/* Read-only extra fields */}
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    {detailOrder.externalReference && (
                      <Typography variant="body2">Oferta: <b>{detailOrder.externalReference}</b></Typography>
                    )}
                    {detailOrder.receiptNumber && (
                      <Typography variant="body2">Factura: <b>{detailOrder.receiptNumber}</b></Typography>
                    )}
                    {detailOrder.deliverySlipReference && (
                      <Typography variant="body2">Remito: <b>{detailOrder.deliverySlipReference}</b></Typography>
                    )}
                    {detailOrder.paymentMethod && (
                      <Typography variant="body2">Condicion Pago: <b>{detailOrder.paymentMethod}</b></Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    {detailOrder.closeDate && (
                      <Typography variant="body2">Fecha de Cierre: <b>{fmtDate(detailOrder.closeDate)}</b></Typography>
                    )}
                    {detailOrder.actualDeliveryDate && (
                      <Typography variant="body2">Fecha Entrega: <b>{fmtDate(detailOrder.actualDeliveryDate)}</b></Typography>
                    )}
                    {detailOrder.paymentDueDate && (
                      <Typography variant="body2">Fecha Vto: <b>{fmtDate(detailOrder.paymentDueDate)}</b></Typography>
                    )}
                    {detailOrder.paymentDate && (
                      <Typography variant="body2">Fecha Abono: <b>{fmtDate(detailOrder.paymentDate)}</b></Typography>
                    )}
                    {detailOrder.paidAmount != null && detailOrder.paidAmount > 0 && (
                      <Typography variant="body2">Abono: <b>{fmtCurrency(detailOrder.paidAmount)}</b></Typography>
                    )}
                  </Stack>
                </>
              )}

              <Typography variant="subtitle1" fontWeight={600}>Productos</Typography>
              {editing && (
                <Typography variant="caption" color="text.secondary">
                  Podés modificar cantidades y precios, agregar productos nuevos o eliminarlos.
                </Typography>
              )}
              {!editing ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell>Producto</TableCell>
                        <TableCell align="right">Cant</TableCell>
                        <TableCell align="right">Precio Unit</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(detailOrder.items || []).map(item => (
                        <TableRow key={item.id}>
                          <TableCell>{item.sku || '—'}</TableCell>
                          <TableCell>{item.productName}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{fmtCurrency(item.unitPrice)}</TableCell>
                          <TableCell align="right">{fmtCurrency(item.lineTotal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <>
                  {(() => {
                    const currentItems: EditableItem[] = editedItems ?? (detailOrder.items || []).map(i => ({
                      productId: i.productId,
                      productName: i.productName,
                      sku: i.sku,
                      quantity: i.quantity,
                      unitPrice: i.unitPrice,
                    }));
                    const setItems = (next: EditableItem[]) => setEditedItems(next);
                    return (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>SKU</TableCell>
                              <TableCell>Producto</TableCell>
                              <TableCell align="right" sx={{ width: 110 }}>Cant</TableCell>
                              <TableCell align="right" sx={{ width: 140 }}>Precio Unit</TableCell>
                              <TableCell align="right">Total</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentItems.map((item, idx) => (
                              <TableRow key={`${item.productId ?? 'new'}-${idx}`}>
                                <TableCell>{item.sku || '—'}</TableCell>
                                <TableCell>{item.productName || `#${item.productId}`}</TableCell>
                                <TableCell align="right">
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={item.quantity}
                                    inputProps={{ min: 0, step: 'any' }}
                                    onChange={e => {
                                      const q = Number(e.target.value);
                                      const next = [...currentItems];
                                      next[idx] = { ...item, quantity: isNaN(q) ? 0 : q };
                                      setItems(next);
                                    }}
                                    sx={{ width: 90 }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <TextField
                                    size="small"
                                    type="text"
                                    value={item.unitPrice ?? ''}
                                    inputProps={{ inputMode: 'decimal', pattern: '[0-9.,]*' }}
                                    onChange={e => {
                                      const next = [...currentItems];
                                      next[idx] = { ...item, unitPrice: parseDecimal(e.target.value) };
                                      setItems(next);
                                    }}
                                    sx={{ width: 120 }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {fmtCurrency((item.quantity || 0) * (item.unitPrice || 0))}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton size="small" onClick={() => setItems(currentItems.filter((_, i) => i !== idx))}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Autocomplete
                                    size="small"
                                    sx={{ minWidth: 280 }}
                                    options={productOptions}
                                    getOptionLabel={(o) => typeof o === 'string' ? o : `${o.name}${o.sku ? ' — ' + o.sku : ''}`}
                                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                    value={productOptions.find(p => p.id === newItemDraft.productId) || null}
                                    onChange={(_, v) => setNewItemDraft(d => ({
                                      ...d,
                                      productId: v?.id ?? null,
                                      productName: v?.name,
                                      sku: v?.sku,
                                      // Only auto-fill the price when the user hasn't typed one yet.
                                      // Otherwise switching products would silently overwrite a manual price.
                                      unitPrice: d.unitPrice != null ? d.unitPrice : (v?.price ?? null),
                                    }))}
                                    renderInput={(params) => <TextField {...params} label="Agregar producto" />}
                                  />
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Cant"
                                    value={newItemDraft.quantity}
                                    inputProps={{ min: 0, step: 'any' }}
                                    onChange={e => setNewItemDraft(d => ({ ...d, quantity: Number(e.target.value) || 0 }))}
                                    sx={{ width: 90 }}
                                  />
                                  <TextField
                                    size="small"
                                    type="text"
                                    label="Precio"
                                    value={newItemDraft.unitPrice ?? ''}
                                    inputProps={{ inputMode: 'decimal', pattern: '[0-9.,]*' }}
                                    onChange={e => setNewItemDraft(d => ({ ...d, unitPrice: parseDecimal(e.target.value) }))}
                                    sx={{ width: 120 }}
                                  />
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    disabled={!newItemDraft.productId || !newItemDraft.quantity}
                                    onClick={() => {
                                      if (!newItemDraft.productId) return;
                                      // If the product is already in the list, merge quantities
                                      // instead of creating a duplicate row.
                                      const existingIdx = currentItems.findIndex(i => i.productId === newItemDraft.productId);
                                      if (existingIdx >= 0) {
                                        const next = [...currentItems];
                                        const prev = next[existingIdx];
                                        next[existingIdx] = {
                                          ...prev,
                                          quantity: (prev.quantity || 0) + (newItemDraft.quantity || 0),
                                          unitPrice: newItemDraft.unitPrice ?? prev.unitPrice,
                                        };
                                        setItems(next);
                                      } else {
                                        setItems([
                                          ...currentItems,
                                          {
                                            productId: newItemDraft.productId,
                                            productName: newItemDraft.productName,
                                            sku: newItemDraft.sku,
                                            quantity: newItemDraft.quantity,
                                            unitPrice: newItemDraft.unitPrice,
                                          },
                                        ]);
                                      }
                                      setNewItemDraft({ productId: null, quantity: 1, unitPrice: null });
                                    }}
                                  >
                                    Agregar
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </>
              )}

              {!editing && detailOrder.notes && (
                <Typography variant="body2" color="text.secondary">Notas: {detailOrder.notes}</Typography>
              )}

              {/* Documentos adjuntos */}
              {!editing && (
                <>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600}>Documentos adjuntos</Typography>
                  <Stack spacing={1}>
                    {[
                      { key: 'offer', label: 'Oferta', url: detailOrder.offerFileUrl },
                      { key: 'invoice', label: 'Factura', url: detailOrder.invoiceFileUrl },
                      { key: 'deliverySlip', label: 'Remito', url: detailOrder.deliverySlipFileUrl },
                    ].map(doc => (
                      <Stack key={doc.key} direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ minWidth: 80 }}>{doc.label}:</Typography>
                        {doc.url ? (
                          <>
                            <Button size="small" onClick={async () => {
                              try {
                                const blob = await api.downloadOrderFile(detailOrder.id, doc.key);
                                if (!blob || blob.size === 0) { setError(`${doc.label} vacío`); return; }
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url; a.download = `${doc.label}-${detailOrder.id}.pdf`;
                                document.body.appendChild(a); a.click(); a.remove();
                                setTimeout(() => window.URL.revokeObjectURL(url), 0);
                              } catch (err: any) {
                                setError(err?.response?.data?.message || `Error al descargar ${doc.label}`);
                              }
                            }}>Descargar</Button>
                            <Button size="small" color="error" onClick={async () => {
                              await api.deleteOrderFile(detailOrder.id, doc.key);
                              await openOrderDetail({ id: detailOrder.id } as any);
                            }}>Quitar</Button>
                          </>
                        ) : (
                          <Button size="small" variant="outlined" component="label">
                            Adjuntar PDF
                            <input hidden type="file" accept="application/pdf" onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = async () => {
                                const base64 = (reader.result as string).split(',')[1] || '';
                                await api.uploadOrderFile(detailOrder.id, { fileType: doc.key, fileName: file.name, base64 });
                                await openOrderDetail({ id: detailOrder.id } as any);
                              };
                              reader.readAsDataURL(file);
                            }} />
                          </Button>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}

              <Divider />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {editing ? (
                  <>
                    <Button variant="outlined" onClick={cancelEditing} disabled={editSaving}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSaveEdit} disabled={editSaving}>
                      {editSaving ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </>
                ) : (
                  <Button color="error" variant="outlined" onClick={() => handleDeleteOrder(detailOrder.id)}>
                    Eliminar venta
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetailOpen(false); cancelEditing(); }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <RegisterPaymentDialog
        open={paymentOpen}
        title={detailOrder ? `Registrar pago — Venta ${detailOrder.reference || ''}` : 'Registrar pago'}
        total={detailOrder?.total || 0}
        paidAmount={detailOrder?.paidAmount || 0}
        onClose={() => setPaymentOpen(false)}
        onConfirm={async (payload) => {
          if (!detailOrder) return;
          await api.registerOrderPayment(detailOrder.id, payload);
          const refreshed = await api.getOrder(detailOrder.id);
          setDetailOrder(refreshed);
          await loadOrders();
        }}
      />

      {/* Save view dialog */}
      <Dialog
        open={saveViewDialogOpen}
        onClose={() => setSaveViewDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Guardar vista</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nombre de la vista"
            value={newViewName}
            onChange={e => setNewViewName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveViewDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveViewConfirm} variant="contained" disabled={!newViewName.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de facturación electrónica (ARCA) */}
      <InvoiceDialog
        open={invoiceOpen}
        orderIds={invoiceOrderIds}
        onClose={() => setInvoiceOpen(false)}
        onEmitted={() => { setSelectedIds(new Set()); loadOrders(); }}
      />

      {/* Marcar en bloque ventas viejas como "Entregado", sin tocar stock */}
      <Dialog open={bulkDeliverOpen} onClose={closeBulkDeliverDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Marcar ventas viejas como entregadas</DialogTitle>
        <DialogContent>
          {bulkDeliverResult !== null ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Listo: se marcaron {bulkDeliverResult} venta(s) como "Entregado". El stock no se tocó.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                Esto marca como "Entregado" todas las ventas cerradas antes de la fecha elegida que
                todavía figuran pendientes de entrega. No descuenta ni modifica stock: es solo para
                que dejen de aparecer como pendientes.
              </Typography>
              <TextField
                type="date"
                label="Marcar entregadas las cerradas antes de"
                value={bulkDeliverCutoff}
                onChange={e => setBulkDeliverCutoff(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {bulkDeliverResult !== null ? (
            <Button onClick={closeBulkDeliverDialog} variant="contained">Cerrar</Button>
          ) : (
            <>
              <Button onClick={closeBulkDeliverDialog} disabled={bulkDeliverSaving}>Cancelar</Button>
              <Button
                onClick={handleBulkMarkDelivered}
                variant="contained"
                disabled={!bulkDeliverCutoff || bulkDeliverSaving}
              >
                {bulkDeliverSaving ? 'Marcando...' : 'Confirmar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <NewSaleDialog open={newSaleOpen} onClose={() => setNewSaleOpen(false)} onSaved={() => { loadOrders(); refreshPendingDeliveries(); }} />
      <PendingDeliveriesDialog open={deliveriesOpen} onClose={() => setDeliveriesOpen(false)} onChanged={() => { loadOrders(); refreshPendingDeliveries(); }} />
    </Box>
  );
};

export default SalesPage;
